import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { gestApi } from "../GestAuthContext";
import { money } from "./constants";

const PAYMENT_METHODS = [
  ["bonifico", "Bonifico"], ["contanti", "Contanti"], ["assegno", "Assegno"],
  ["pos", "POS / Carta"], ["ricevuta_bancaria", "Ri.Ba."], ["altro", "Altro"],
];

export default function IncassoFlow({ onSaved }) {
  const [cantieri, setCantieri] = useState([]);
  const [selectedCid, setSelectedCid] = useState("");
  const [incassi, setIncassi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, i] = await Promise.all([
        gestApi.get("/cantieri"),
        gestApi.get(selectedCid ? `/incassi?cantiere_id=${selectedCid}` : "/incassi"),
      ]);
      setCantieri(c.data);
      setIncassi(i.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  }, [selectedCid]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({
      cantiere_id: selectedCid || "",
      importo_fatturato: "",
      importo_incassato: "",
      data_fattura: new Date().toISOString().slice(0, 10),
      data_incasso: "",
      numero_fattura: "",
      metodo_pagamento: "bonifico",
      note: "",
    });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      cantiere_id: row.cantiere_id,
      importo_fatturato: row.importo_fatturato,
      importo_incassato: row.importo_incassato,
      data_fattura: (row.data_fattura || "").slice(0, 10),
      data_incasso: (row.data_incasso || "").slice(0, 10),
      numero_fattura: row.numero_fattura || "",
      metodo_pagamento: row.metodo_pagamento || "bonifico",
      note: row.note || "",
    });
    setShowForm(true);
  };

  const updateField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const fatt = Number(form.importo_fatturato || 0);
    const inc = Number(form.importo_incassato || 0);
    if (!form.cantiere_id) return toast.error("Seleziona un cantiere");
    if (fatt <= 0) return toast.error("Importo fatturato obbligatorio");
    if (inc > fatt + 0.01) return toast.error("L'incasso non può superare il fatturato");
    setBusy(true);
    try {
      const payload = {
        cantiere_id: form.cantiere_id,
        importo_fatturato: fatt,
        importo_incassato: inc,
        data_fattura: form.data_fattura || null,
        data_incasso: form.data_incasso || null,
        numero_fattura: form.numero_fattura || "",
        metodo_pagamento: form.metodo_pagamento || "",
        note: form.note || "",
      };
      if (editing) {
        // PATCH allows partial update — but keep semantic clean: send incasso fields only if changed
        await gestApi.patch(`/incassi/${editing.id}`, {
          importo_incassato: payload.importo_incassato,
          data_incasso: payload.data_incasso,
          metodo_pagamento: payload.metodo_pagamento,
          note: payload.note,
        });
        toast.success("Incasso aggiornato");
      } else {
        await gestApi.post("/incassi", payload);
        toast.success("Fattura & incasso registrati");
      }
      setShowForm(false);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Eliminare la fattura ${row.numero_fattura || `da ${money(row.importo_fatturato)}`}?`)) return;
    try {
      await gestApi.delete(`/incassi/${row.id}`);
      toast.success("Eliminata");
      await load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // Aggregates for the currently filtered view (or all)
  const totFatt = incassi.reduce((s, r) => s + (r.importo_fatturato || 0), 0);
  const totInc = incassi.reduce((s, r) => s + (r.importo_incassato || 0), 0);
  const totDa = totFatt - totInc;
  const incassoPct = totFatt ? Math.round((totInc / totFatt) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="gest-incasso-flow">
      {/* Filter + totali */}
      <div className="bg-white rounded-2xl p-5 border border-black/[0.04] shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-[13px] font-semibold text-[#0a1420]">Fatture & Incassi</div>
            <div className="text-[11px] text-[#6e6e73] mt-0.5">Traccia quanto è stato fatturato al cliente e quanto è già entrato.</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedCid}
              onChange={(e) => setSelectedCid(e.target.value)}
              data-testid="gest-incasso-filter"
              className="border border-black/10 rounded-lg px-2.5 py-2 text-[13px] bg-white min-w-[180px]"
            >
              <option value="">Tutti i cantieri</option>
              {cantieri.map((c) => <option key={c.id} value={c.id}>{`${c.nome} · ${c.cliente || ""}`}</option>)}
            </select>
            <button onClick={openNew} data-testid="gest-incasso-new" className="px-3 py-2 bg-[#0d9488] text-white rounded-lg text-[13px] font-semibold hover:bg-[#0f766e]">
              + Nuova fattura
            </button>
          </div>
        </div>
        {/* Totali summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#f5f7fb] rounded-xl p-3">
            <div className="text-[10.5px] text-[#6e6e73] uppercase tracking-widest">Fatturato</div>
            <div className="text-[18px] font-semibold text-[#0a1420] mt-0.5">{money(totFatt)}</div>
          </div>
          <div className="bg-[#0d9488]/5 rounded-xl p-3">
            <div className="text-[10.5px] text-[#0d9488] uppercase tracking-widest">Incassato</div>
            <div className="text-[18px] font-semibold text-[#0d9488] mt-0.5">{money(totInc)}</div>
          </div>
          <div className={`rounded-xl p-3 ${totDa > 0 ? "bg-orange-50" : "bg-green-50"}`}>
            <div className={`text-[10.5px] uppercase tracking-widest ${totDa > 0 ? "text-orange-600" : "text-green-700"}`}>Da incassare</div>
            <div className={`text-[18px] font-semibold mt-0.5 ${totDa > 0 ? "text-orange-600" : "text-green-700"}`}>{money(totDa)}</div>
          </div>
        </div>
        <div className="mt-3 h-2 bg-[#f5f7fb] rounded-full overflow-hidden">
          <div className="h-2 bg-[#0d9488] transition-all" style={{ width: `${incassoPct}%` }} />
        </div>
        <div className="text-[10.5px] text-[#6e6e73] mt-1 text-right">{incassoPct}% incassato</div>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-2xl border border-black/[0.04] shadow-sm overflow-hidden" data-testid="gest-incasso-list">
        {loading ? (
          <div className="p-6 text-[13px] text-[#6e6e73] text-center">Caricamento…</div>
        ) : incassi.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-[26px] mb-2 opacity-40">€</div>
            <div className="text-[13px] text-[#6e6e73]">Nessuna fattura registrata.</div>
            <button onClick={openNew} className="mt-3 text-[12px] text-[#0d9488] font-semibold">Registra la prima fattura →</button>
          </div>
        ) : (
          <div className="divide-y divide-black/5">
            <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_100px] gap-3 px-4 py-3 text-[10.5px] uppercase tracking-widest text-[#6e6e73] bg-[#fafbfc]">
              <div>Cantiere / N° fattura</div>
              <div className="text-right">Fatturato</div>
              <div className="text-right">Incassato</div>
              <div className="text-right">Da incassare</div>
              <div>Data / Metodo</div>
              <div></div>
            </div>
            {incassi.map((r) => {
              const cn = cantieri.find((c) => c.id === r.cantiere_id)?.nome || "—";
              const overdue = r.da_incassare > 0 && r.data_fattura && (new Date() - new Date(r.data_fattura)) / 86400000 > 60;
              return (
                <div key={r.id} className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_100px] gap-2 md:gap-3 px-4 py-3 items-center hover:bg-[#fafbfc]" data-testid={`gest-incasso-row-${r.id}`}>
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-[#0a1420] truncate">{cn}</div>
                    <div className="text-[11.5px] text-[#6e6e73] truncate">
                      {r.numero_fattura ? `#${r.numero_fattura}` : "senza n°"}
                      {r.data_fattura ? ` · ${new Date(r.data_fattura).toLocaleDateString("it-IT")}` : ""}
                    </div>
                  </div>
                  <div className="text-right text-[13px] text-[#0a1420]">{money(r.importo_fatturato)}</div>
                  <div className="text-right text-[13px] text-[#0d9488] font-medium">{money(r.importo_incassato)}</div>
                  <div className={`text-right text-[13px] font-semibold ${r.da_incassare > 0 ? (overdue ? "text-red-600" : "text-orange-600") : "text-green-700"}`}>
                    {r.da_incassare > 0 ? money(r.da_incassare) : "✓ saldata"}
                    {overdue && <div className="text-[10px] font-normal text-red-500">oltre 60g</div>}
                  </div>
                  <div className="text-[11.5px] text-[#6e6e73]">
                    {r.data_incasso ? new Date(r.data_incasso).toLocaleDateString("it-IT") : "—"}
                    {r.metodo_pagamento ? ` · ${r.metodo_pagamento}` : ""}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(r)} data-testid={`gest-incasso-edit-${r.id}`} className="text-[11px] text-[#0a1420] hover:underline">Aggiorna</button>
                    <button onClick={() => del(r)} data-testid={`gest-incasso-del-${r.id}`} className="text-[11px] text-red-500 hover:text-red-700">Elimina</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Form modale */}
      {showForm && form && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={save} className="w-full max-w-[560px] bg-white rounded-2xl overflow-hidden shadow-xl max-h-[90vh] flex flex-col" data-testid="gest-incasso-form">
            <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
              <div className="text-[15px] font-semibold text-[#0a1420]">{editing ? "Aggiorna incasso" : "Nuova fattura & incasso"}</div>
              <button type="button" onClick={() => setShowForm(false)} className="text-[#6e6e73] hover:text-[#0a1420] text-[20px] leading-none">×</button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-[12px] text-[#6e6e73] mb-1">Cantiere *</label>
                <select required disabled={!!editing} value={form.cantiere_id} onChange={(e) => updateField("cantiere_id", e.target.value)} data-testid="gest-incasso-cantiere" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] bg-white disabled:bg-[#f5f7fb]">
                  <option value="">— Seleziona —</option>
                  {cantieri.map((c) => <option key={c.id} value={c.id}>{`${c.nome} · ${c.cliente || ""}`}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] text-[#6e6e73] mb-1">N° fattura</label>
                  <input type="text" value={form.numero_fattura} onChange={(e) => updateField("numero_fattura", e.target.value)} disabled={!!editing} placeholder="es. 2026/145" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] disabled:bg-[#f5f7fb]" />
                </div>
                <div>
                  <label className="block text-[12px] text-[#6e6e73] mb-1">Data fattura</label>
                  <input type="date" value={form.data_fattura} onChange={(e) => updateField("data_fattura", e.target.value)} disabled={!!editing} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] disabled:bg-[#f5f7fb]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] text-[#6e6e73] mb-1">Importo fatturato (€) *</label>
                  <input required type="number" min="0.01" step="0.01" value={form.importo_fatturato} onChange={(e) => updateField("importo_fatturato", e.target.value)} disabled={!!editing} data-testid="gest-incasso-fatturato" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] font-mono disabled:bg-[#f5f7fb]" />
                </div>
                <div>
                  <label className="block text-[12px] text-[#0d9488] font-medium mb-1">Importo incassato (€)</label>
                  <input type="number" min="0" step="0.01" max={form.importo_fatturato || undefined} value={form.importo_incassato} onChange={(e) => updateField("importo_incassato", e.target.value)} data-testid="gest-incasso-incassato" className="w-full border border-[#0d9488]/30 rounded-lg px-3 py-2 text-[14px] font-mono bg-[#0d9488]/[0.03]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] text-[#6e6e73] mb-1">Data incasso</label>
                  <input type="date" value={form.data_incasso} onChange={(e) => updateField("data_incasso", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px]" />
                </div>
                <div>
                  <label className="block text-[12px] text-[#6e6e73] mb-1">Metodo pagamento</label>
                  <select value={form.metodo_pagamento} onChange={(e) => updateField("metodo_pagamento", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] bg-white">
                    {PAYMENT_METHODS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              {form.importo_fatturato && Number(form.importo_incassato || 0) < Number(form.importo_fatturato) && (
                <div className="text-[12px] p-2.5 rounded-lg bg-orange-50 border border-orange-100 text-orange-700">
                  Da incassare: <span className="font-semibold">{money(Number(form.importo_fatturato) - Number(form.importo_incassato || 0))}</span>
                </div>
              )}
              <div>
                <label className="block text-[12px] text-[#6e6e73] mb-1">Note</label>
                <textarea rows={2} value={form.note} onChange={(e) => updateField("note", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] resize-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-black/5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-[13px] text-[#6e6e73]">Annulla</button>
              <button type="submit" disabled={busy} data-testid="gest-incasso-save" className="px-5 py-2 bg-[#0d9488] text-white rounded-lg text-[13px] font-semibold hover:bg-[#0f766e] disabled:opacity-60">
                {busy ? "Salvataggio…" : (editing ? "Aggiorna" : "Registra fattura")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

